import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import DocumentsIcon from './icons/DocumentsIcon';
import MapIcon from './icons/MapIcon';
import CalendarHeartIcon from './icons/CalendarHeartIcon';
import { Breakpoint } from '@mui/material/styles';
import useAppState from '../../common/state/appState';

type MaxBadgeSize = 'md' | 'lg';

interface IIconProps {
    maxBadgeSize: MaxBadgeSize;
}

interface ICardContent {
    icon: React.ComponentType<IIconProps>;
    text: string;
    disclaimer?: string;
}

const cardContentItems: ICardContent[] = [
    {
        icon: DocumentsIcon,
        text: 'Upfront prices, CarMax Certified quality, and detailed history reports on every car',
    },
    {
        icon: MapIcon,
        text: 'The convenience of online shopping with the ability to test drive before buying. We offer no-pressure support along the way',
    },
    {
        icon: CalendarHeartIcon,
        text: '10-day returns and 30-day limited warranty (longer in select states)',
        disclaimer: 'See store for details',
    },
];

const cardContentItemsUFT: ICardContent[] = [
    {
        icon: DocumentsIcon,
        text: "Know what you're paying, what you're getting, and see the car's history",
    },
    {
        icon: MapIcon,
        text: "Shop online, in-store or both - we're here to help when you need it",
    },
    {
        icon: CalendarHeartIcon,
        text: '10-day returns and 30-day limited warranty (longer in some states)',
        disclaimer: 'See store for details',
    },
];

interface ICarMaxDifferenceCardProps {
    item: ICardContent;
    maxWidth?: Breakpoint;
    maxBadgeSize: MaxBadgeSize;
}

const CarMaxDifferenceCard = ({ item, maxBadgeSize }: ICarMaxDifferenceCardProps) => {
    return (
        <Card
            elevation={0}
            sx={(theme) => ({
                backgroundColor: `${theme.palette.secondary.faint}`,
                borderRadius: 1.5,
                flex: '1 1 auto',
            })}
        >
            <Stack
                direction={{ xs: 'row', sm: 'column' }}
                spacing={{ xs: 3, sm: 2, md: 3 }}
                sx={{
                    alignItems: { xs: 'center', md: 'flex-start' },
                    pt: { xs: 3, sm: 4 },
                    pr: { xs: 3, md: 5 },
                    pb: { xs: 3 },
                    pl: { xs: 3, md: 5 },
                    ...(maxBadgeSize === 'md' && {
                        pt: { xs: 3, sm: 4 },
                        pr: { xs: 3, sm: 4 },
                        pb: { xs: 3, sm: 3 },
                        pl: { xs: 3, sm: 4 },
                    }),
                }}
            >
                <Box
                    sx={{
                        width: { sm: '100%' },
                        display: 'flex',
                    }}
                >
                    <item.icon maxBadgeSize={maxBadgeSize} />
                </Box>
                <Box sx={{ maxWidth: '17rem' }}>
                    <Typography
                        sx={{
                            ...(maxBadgeSize === 'lg' && {
                                typography: { md: 'bodyLarge' },
                            }),
                        }}
                    >
                        {item.text}
                    </Typography>
                    {item.disclaimer && (
                        <Typography
                            component="p"
                            variant="caption"
                            color="textSecondary"
                            fontStyle="italic"
                            sx={{ mt: 0.5 }}
                        >
                            {item.disclaimer}
                        </Typography>
                    )}
                </Box>
            </Stack>
        </Card>
    );
};

interface ITheCarMaxDifference {
    maxBadgeSize: MaxBadgeSize;
}

export const TheCarMaxDifference = ({ maxBadgeSize }: ITheCarMaxDifference) => {
    const serveUFTContent = useAppState((state) => state.serveUFTContent);
    const cardItems = serveUFTContent ? cardContentItemsUFT : cardContentItems;

    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{
                xs: 1.5,
                ...(maxBadgeSize === 'lg' && { md: 3 }),
            }}
        >
            {cardItems.map((cardItem) => (
                <CarMaxDifferenceCard key={cardItem.text} item={cardItem} maxBadgeSize={maxBadgeSize} />
            ))}
        </Stack>
    );
};
